/**
 * ReviewRegenerateDialog — Patch 37.4.21.
 *
 * In-context regeneration flow for a rendered deliverable sitting in
 * ``needs_review``. Never fires a paid render on click — the founder
 * MUST acknowledge cost + optionally supply feedback before we hand
 * off to the backend regenerate endpoint. The backend resolves the
 * canonical source spec via lineage; we never regenerate from the
 * rendered head as though it were the source prompt.
 *
 * Secondary option: "Open in AI Studio" for deeper editing.
 */
import { useEffect, useState } from "react";

import { Button } from "@/shared/ui/button";

interface Props {
  open: boolean;
  isPending: boolean;
  permanentAssetId: string | null;
  onCancel: () => void;
  onConfirm: (payload: { feedback_note: string | null; cost_ack: true }) => void;
  onOpenAiStudio: () => void;
}

export function ReviewRegenerateDialog({
  open, isPending, permanentAssetId, onCancel, onConfirm, onOpenAiStudio,
}: Props): JSX.Element | null {
  const [note, setNote] = useState<string>("");
  const [ack, setAck] = useState<boolean>(false);

  // Reset transient state whenever the dialog is re-opened.
  useEffect(() => {
    if (open) {
      setNote("");
      setAck(false);
    }
  }, [open]);

  if (!open) return null;

  const disabled = !ack || isPending;

  return (
    <div
      data-testid="review-regenerate-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="review-regenerate-title"
    >
      <div className="max-w-lg w-full rounded-2xl border border-white/10 bg-[color:var(--brand-charcoal,#12151C)] p-6 shadow-xl">
        <h3
          id="review-regenerate-title"
          className="text-lg font-semibold text-white"
        >
          Regenerate final media
        </h3>
        <p className="mt-2 text-sm text-white/70">
          We'll use the canonical source specification for{" "}
          {permanentAssetId ? (
            <span className="font-mono text-white/85">{permanentAssetId}</span>
          ) : (
            "this asset"
          )}{" "}
          to produce a fresh version. The current rendered version stays in
          history — it is not deleted.
        </p>

        <label className="mt-4 block text-xs uppercase tracking-widest text-white/50">
          Feedback (optional)
        </label>
        <textarea
          data-testid="review-regenerate-note"
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 1000))}
          rows={3}
          placeholder="e.g. brighten the scene, shift camera left, warmer palette…"
          className="mt-1 w-full rounded-md border border-white/10 bg-black/40 p-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-cyan-400"
        />
        <div className="mt-1 text-[10px] text-white/40">
          Feedback is captured on the asset's history for context — it does
          not change the canonical prompt.
        </div>

        <label
          className="mt-4 flex items-start gap-2 text-sm text-white/85"
          data-testid="review-regenerate-cost-ack-row"
        >
          <input
            type="checkbox"
            data-testid="review-regenerate-cost-ack"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            I acknowledge this uses AI production capacity and may incur
            provider cost.
          </span>
        </label>

        <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
          <Button
            data-testid="review-regenerate-open-ai-studio"
            variant="outline"
            onClick={onOpenAiStudio}
            disabled={isPending}
          >
            Open in AI Studio
          </Button>
          <div className="flex-1" />
          <Button
            data-testid="review-regenerate-cancel"
            variant="ghost"
            onClick={onCancel}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            data-testid="review-regenerate-confirm"
            disabled={disabled}
            onClick={() =>
              onConfirm({
                feedback_note: note.trim() ? note.trim() : null,
                cost_ack: true,
              })
            }
          >
            {isPending ? "Regenerating…" : "Regenerate"}
          </Button>
        </div>
      </div>
    </div>
  );
}
